// Full-screen preview for the page designer.
//
// Renders the saved page with the native React runtime (SmartPageRuntime),
// replacing the old Angular `/smart-page/index.html` iframe. The caller
// (PageDesignerPage.handlePreview) saves the page first, so we load the
// persisted object from the backend and render it stateless in preview mode.

import { useEffect, useMemo, useState } from 'react'
import { X, Loader2 } from 'lucide-react'

import { ThemedPageShell } from '@twasta/page-runtime'
import { loadPageObject } from '@/api/designers'
import { toV2, normalizePageKind } from './migrate'
import { resolveTheme } from './theme'
import { viewportWidth } from './DeviceToggle'
import SmartPageRuntime from '@/pages/workbench/smartpage-preview/SmartPageRuntime'
import type { PageModel, Viewport } from './types'

interface PreviewModalProps {
  open: boolean
  onClose: () => void
  projectName: string
  objectName: string
  viewport?: Viewport
}

export default function PreviewModal({ open, onClose, projectName, objectName, viewport = 'desktop' }: PreviewModalProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', flexDirection: 'column',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        style={{
          flex: 1, margin: 16, borderRadius: 8, overflow: 'hidden',
          background: 'var(--color-bg)', display: 'flex', flexDirection: 'column',
          boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: '0.9125rem', fontWeight: 500, color: 'var(--color-text)' }}>
              Preview
            </span>
            <span style={{ opacity: 0.5, fontSize: '0.8625rem' }}>—</span>
            <span style={{
              fontSize: '0.8625rem', color: 'var(--color-text-secondary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {projectName} / {objectName}
            </span>
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            title="Close (Esc)"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--color-bg)' }}>
          <PreviewBody project={projectName} objName={objectName} viewport={viewport} />
        </div>
      </div>
    </div>
  )
}

// Loads the saved page object and renders it with the React runtime, themed
// to the author's palette and constrained to the chosen device width.
function PreviewBody({ project, objName, viewport }: { project: string; objName: string; viewport: Viewport }) {
  const [page, setPage] = useState<PageModel | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setPage(null)
    setLoadError(null)
    loadPageObject(project, objName).then(
      (data) => {
        if (cancelled) return
        // Backend returns { content: PageRoot } or { page: ... }; normalize.
        const root = (data?.content ?? data) as { page?: PageModel } | PageModel
        const raw: PageModel = (root && 'page' in root && root.page ? root.page : root) as PageModel
        if (!raw || typeof raw !== 'object') {
          setLoadError('Page object did not include a page model.')
          return
        }
        setPage(normalizePageKind(toV2(raw)))
      },
      (err: unknown) => {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String(err)
        setLoadError(msg || 'Failed to load page')
      },
    )
    return () => { cancelled = true }
  }, [project, objName])

  const theme = useMemo(() => resolveTheme(page?.theme), [page?.theme])

  if (loadError) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 200, padding: 24, textAlign: 'center',
        color: 'var(--pg-error, #c33)', fontSize: 14,
      }}>
        {loadError}
      </div>
    )
  }

  if (!page) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, minHeight: 200, color: 'var(--color-text-secondary)', fontSize: 14,
      }}>
        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
        Loading preview…
      </div>
    )
  }

  // Document-kind pages render their template in stateless preview mode here
  // (the designer modal is for previewing the page layout, not authoring doc
  // instances — that lives in the workbench Live Preview tab).
  // constrain ON (default): cap content at the theme's content-width + padding
  // and center it, framed by the page background — matching the design canvas
  // and the deployed runtime.
  const frame = (
    <ThemedPageShell theme={theme} minHeight="100%">
      <SmartPageRuntime page={page} mode="preview" docId={null} project={project} viewport={viewport} />
    </ThemedPageShell>
  )

  if (viewport === 'desktop') return frame
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 16, background: 'var(--color-bg-secondary)' }}>
      <div style={{ width: viewportWidth(viewport), maxWidth: '100%' }}>{frame}</div>
    </div>
  )
}
